import { Link, useParams, useSearchParams } from 'react-router-dom';

import { Badge, Card, Icon } from '@/components/ui';
import { useT } from '@/lib/i18n';
import { useAsyncData } from '@/lib/hooks/useAsyncData';

import { fetchDish, formatPrice, type DishDetailDto } from './api';

export function DishScreen({ shopSlug }: { shopSlug: string }) {
  const t = useT();
  const { dishId = '' } = useParams();
  const [searchParams] = useSearchParams();
  const lang = searchParams.get('lang') ?? '';

  const { data: dish, error, loading } = useAsyncData<DishDetailDto>(
    dishId ? `${shopSlug}|${dishId}|${lang}` : null,
    (signal) => fetchDish(shopSlug, dishId, lang, signal),
    t('dish.loadFailed'),
  );

  const backTo = `/shop/${shopSlug}/menu${lang ? `?lang=${lang}` : ''}`;

  if (loading) {
    return (
      <main className="mx-auto max-w-[460px] px-4 py-6" aria-busy="true">
        <span className="sr-only">{t('dish.loading')}</span>
        <div className="h-6 w-32 animate-pulse rounded bg-ink-100" />
        <div className="mt-4 h-56 animate-pulse rounded-[var(--radius-lg)] bg-ink-100" />
        <div className="mt-4 h-24 animate-pulse rounded-[var(--radius-lg)] bg-ink-100" />
      </main>
    );
  }

  if (error || !dish) {
    return (
      <main className="mx-auto max-w-[420px] px-6 py-16">
        <Card pad>
          <h1 className="text-h3 text-plum-900">{t('dish.unavailable')}</h1>
          <p className="text-body-s mt-1 text-ink-600">{error}</p>
          <Link to={backTo} className="text-body-s mt-4 inline-block font-semibold text-plum-700">
            {t('dish.backToMenu')}
          </Link>
        </Card>
      </main>
    );
  }

  const price = formatPrice(
    dish.price_amount,
    dish.currency,
    dish.tax_included,
    t('menu.taxIncluded'),
    t('menu.taxExcluded'),
  );

  return (
    <main className="mx-auto max-w-[460px] bg-paper pb-10">
      <header className="flex items-center gap-3 border-b border-line px-4 py-3">
        <Link to={backTo} className="text-ink-700" aria-label={t('dish.backToMenu')}>
          <Icon name="arrow-left" />
        </Link>
        <h1 className="text-body-s min-w-0 flex-1 truncate font-semibold text-plum-900">
          {dish.name}
        </h1>
        {dish.fallback_applied ? (
          <Badge tone="warn">
            <Icon name="info" size={11} />
            {t('dish.translated')}
          </Badge>
        ) : null}
      </header>

      <div className="px-4 py-4">
        {dish.image_url ? (
          <img
            src={dish.image_url}
            alt=""
            className="h-52 w-full rounded-[var(--radius-lg)] object-cover sm:h-64 lg:h-80"
            aria-hidden="true"
          />
        ) : null}

        <h2 className="text-h2 mt-4 text-plum-900">{dish.name}</h2>
        {price ? (
          <p className="text-body-l mt-1 font-semibold text-ink-800">{price}</p>
        ) : null}

        <div className="mt-5 space-y-3">
          <DetailCard title={t('dish.description')} body={dish.description} />
          <DetailCard title={t('dish.materials')} body={dish.ingredients_text} />
          <DetailCard title={t('dish.preparation')} body={dish.cooking_instructions} />

          
        </div>

        <p className="text-caption mt-5 text-ink-500">{t('dish.allergenNote')}</p>
      </div>
    </main>
  );
}

function DetailCard({ title, body }: { title: string; body: string | null }) {
  if (!body) return null;

  return (
    <Card pad>
      <h3 className="text-body-s flex items-center gap-2 font-semibold text-ink-800">
        <span className="h-1.5 w-1.5 rounded-full bg-plum-700" aria-hidden="true" />
        {title}
      </h3>
      <p className="text-body-s mt-1.5 whitespace-pre-line text-ink-700">{body}</p>
    </Card>
  );
}
